'use client';

import { useWithSound } from '@repo/react-hooks';
import { formatAmount } from '@repo/ui/amount-text';
import { useAmountText } from '@repo/ui/amount-text/context';
import { toaster } from '@repo/ui/toast';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { FC, PropsWithChildren, startTransition, useEffect } from 'react';
import { APP_REVALIDATE_QUERY_NAMES } from '@/constants';
import useSseWithWalletBalance from '@/hooks/useSseWithWalletBalance';
import { useAppStore } from '@/providers/AppStoreProvider';

/**
 * Provider to handle SSE events for wallet balance changes.
 *
 * @param children
 * @returns
 */
const SseProvider: FC<PropsWithChildren> = ({ children }) => {
  const { accessToken } = useAppStore((state) => ({ accessToken: state.accessToken }));
  const { playSound } = useWithSound('/sounds/notification-sound-2.mp3');
  const { useSymbol } = useAmountText();
  const t = useTranslations('wallet-balance');
  const queryClient = useQueryClient();

  const { startEventSource } = useSseWithWalletBalance((walletBalance) => {
    startTransition(async () => {
      // If the the change amount is 0, we don't need to invalidate the query
      // because the game balance is not changing
      if (walletBalance.changeAmount === 0) {
        return;
      }

      // Invalidate the wallet balance query to get the latest data
      //
      // All the components that use the wallet balance query will be re-rendered
      queryClient.invalidateQueries({
        queryKey: [APP_REVALIDATE_QUERY_NAMES.WALLET_BALANCE],
      });

      if (walletBalance.eventType === 'deposit') {
        toaster({
          status: 'success',
          title: t('title'),
          description: t(`successful-description-${walletBalance.eventType}`, {
            amount: formatAmount(walletBalance.changeAmount, walletBalance.currency, useSymbol),
          }),
        });

        queryClient.invalidateQueries({
          queryKey: [APP_REVALIDATE_QUERY_NAMES.DEPOSITS_HISTORY],
        });

        playSound(); // Play an incoming transaction sound
      }

      // The withdrawal event is coming in
      if (walletBalance.eventType === 'withdrawal' && walletBalance.changeType === 'credit') {
        toaster({
          status: 'info',
          title: t('title'),
          description: t(`successful-description-${walletBalance.eventType}`, {
            amount: formatAmount(walletBalance.changeAmount, walletBalance.currency, useSymbol),
          }),
        });

        queryClient.invalidateQueries({
          queryKey: [APP_REVALIDATE_QUERY_NAMES.WITHDRAWALS_HISTORY],
        });

        playSound(); // Play an incoming transaction sound
      }

      if (walletBalance.eventType === 'bonus-adjustment') {
        toaster({
          status: 'success',
          title: t('title'),
          description: t(`successful-description-${walletBalance.eventType}`, {
            amount: formatAmount(walletBalance.changeAmount, walletBalance.currency, useSymbol),
          }),
        });

        queryClient.invalidateQueries({
          queryKey: [APP_REVALIDATE_QUERY_NAMES.BONUS_HISTORY],
        });

        playSound(); // Play an incoming transaction sound
      }

      if (walletBalance.eventType === 'wallet-adjustment') {
        toaster({
          status: 'success',
          title: t('title'),
          description: t(`successful-description-${walletBalance.eventType}`, {
            amount: formatAmount(walletBalance.changeAmount, walletBalance.currency, useSymbol),
          }),
        });

        playSound(); // Play an incoming transaction sound
      }
    });
  });

  useEffect(() => {
    if (accessToken) {
      const cleanup = startEventSource();
      return cleanup;
    }
  }, [accessToken]);

  return <>{children}</>;
};

export { SseProvider };
